import React                                      from 'react'
import {Box, Button, Typography, useTheme}         from '@mui/material'
import ReactMarkdown                               from 'react-markdown'
import DialogBlurred                               from '../DialogBlurred'
import {severityAccent, severityIcon}              from '../../util/announcement-severity'

/*
 * The announcement as a modal (190).
 *
 * Driven entirely by props (announcement in, onDismiss out) so AnnouncementGate owns the state and
 * this owns only how it looks -- the same split as AchievementToast and TrialBanner.
 *
 * DISMISSAL IS THE ACKNOWLEDGEMENT. There is no separate "acknowledge" control, because the server
 * records the moment it was shown and closed; see service/announcement.js. Closing by any route --
 * the button, the backdrop, Escape -- therefore counts, which is why onClose and the button do the
 * same thing. An announcement nobody can close would trap a student mid-study, and one that is
 * closeable but not recorded would come back every visit.
 */
const AnnouncementDialog = ({announcement, onDismiss}) => {
	const theme = useTheme()
	const custom = theme.custom

	const accent = severityAccent(announcement.severity, custom)
	const Icon = severityIcon(announcement.severity)

	return (
		<DialogBlurred
			open
			onClose={onDismiss}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: `${custom.radius.lg}px`,
					borderTop: `4px solid ${accent}`,
					background: custom.bg.surface,
					p: {xs: 2.5, sm: 3},
				},
			}}
		>
			<Box sx={{display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5}}>
				<Icon sx={{color: accent}}/>
				<Typography sx={{
					fontSize: custom.fontSize.display,
					fontWeight: custom.fontWeight.bold,
					color: custom.text.main,
					lineHeight: 1.3,
				}}>
					{announcement.title}
				</Typography>
			</Box>

			<Box sx={{
				fontSize: custom.fontSize.md,
				color: custom.text.muted,
				lineHeight: 1.6,
				'& p:first-of-type': {mt: 0},
				'& p:last-of-type': {mb: 0},
				'& a': {color: custom.link},
				// a long announcement scrolls rather than pushing the button off a short screen
				maxHeight: '55vh',
				overflowY: 'auto',
			}}>
				<ReactMarkdown>{announcement.body}</ReactMarkdown>
			</Box>

			<Box sx={{display: 'flex', justifyContent: 'flex-end', mt: 3}}>
				<Button variant="contained" disableElevation onClick={onDismiss}>
					Got it
				</Button>
			</Box>
		</DialogBlurred>
	)
}

export default AnnouncementDialog
